// SelfAwarenessQuestions — interactive "Questions to Ask Yourself"
// section that renders below the comparison matrix on a compound /
// program landing page.
//
// Two patterns of question:
//   • static       — expand reveals one explanatory response.
//   • interactive  — expand reveals options; selecting an option
//                    reveals the response for that option.
//
// Multiple questions can be expanded independently.
//
// State / persistence:
//   • Anonymous member  → in-memory state only.
//   • Authenticated     → loads prior answers on mount and
//                          debounce-POSTs the latest map after every
//                          option change (500ms).
//
// Brand visual: cream card, peach left-border accent, chevron that
// rotates on expand. Selected interactive option gets a peach-tinted
// background.
import { useEffect, useMemo, useRef, useState } from "react";
import { apiGet, apiPost, apiPut, getToken } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useAssessment } from "@/lib/assessment";
import { calcBmi, ftInToCm, lbsToKg, cmToFtIn, kgToLbs } from "@/lib/format";
import { C } from "@/lib/brand";

const SAVE_DEBOUNCE_MS = 600;

export default function SelfAwarenessQuestions({ programSlug, programAccent }) {
  const [data, setData] = useState(null); // { showOnMemberPage, questions, ... } | null
  const [expanded, setExpanded] = useState({}); // { [questionId]: bool }
  const [answers, setAnswers] = useState({}); // { [questionId]: optionId | null }
  const { consumer } = useAuth();
  const isAuthed = !!getToken();
  const saveTimer = useRef(null);
  const lastSaved = useRef(null);

  // Fetch questions (public). Bail silently on failure — admin may be
  // mid-deploy on this slug, and we'd rather render nothing than
  // surface an error toast for a non-blocking section.
  useEffect(() => {
    let cancelled = false;
    apiGet(`/consumer/programs/${programSlug}/self-awareness-questions`, { auth: false })
      .then((res) => {
        if (cancelled) return;
        setData(res || null);
      })
      .catch(() => {
        if (cancelled) return;
        setData(null);
      });
    return () => { cancelled = true; };
  }, [programSlug]);

  // Rehydrate prior answers for authenticated members so they continue
  // where they left off across sessions.
  useEffect(() => {
    if (!isAuthed) return;
    let cancelled = false;
    apiGet(`/consumer/programs/${programSlug}/self-awareness-answers`)
      .then((res) => {
        if (cancelled) return;
        const prior = res?.answers && typeof res.answers === "object" ? res.answers : {};
        setAnswers(prior);
        // Auto-expand any question the member has already answered so
        // they see their previous response without an extra tap.
        const expandedFromPrior = {};
        for (const qid of Object.keys(prior)) {
          if (prior[qid]) expandedFromPrior[qid] = true;
        }
        setExpanded((prev) => ({ ...expandedFromPrior, ...prev }));
        lastSaved.current = JSON.stringify(prior);
      })
      .catch(() => { /* silent — start fresh */ });
    return () => { cancelled = true; };
  }, [programSlug, isAuthed]);

  // Auto-expand the static BMI question when the member already has
  // height/weight saved (either in their consumer profile or in
  // localStorage from a prior calc). The static question has no
  // optionId so the answer-rehydrate block above doesn't catch it —
  // but if their numbers are stored, the calc will pre-fill and we
  // want it visible without an extra tap.
  useEffect(() => {
    const hasProfile = !!(consumer?.heightCm && consumer?.weightKg);
    const hasLocal = (() => {
      try {
        const raw = localStorage.getItem("voya9.bmiCalc");
        if (!raw) return false;
        const v = JSON.parse(raw);
        return !!(v?.heightCm && v?.weightKg);
      } catch { return false; }
    })();
    if (hasProfile || hasLocal) {
      setExpanded((prev) => (prev.bmi ? prev : { ...prev, bmi: true }));
    }
  }, [consumer?.heightCm, consumer?.weightKg]);

  // Persist on change (authenticated only). Debounce so rapid toggling
  // collapses into a single network call. The endpoint is idempotent
  // and accepts the full snapshot every call.
  useEffect(() => {
    if (!isAuthed) return undefined;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      const snapshot = JSON.stringify(answers);
      if (snapshot === lastSaved.current) return;
      lastSaved.current = snapshot;
      apiPost(`/consumer/programs/${programSlug}/self-awareness-answers`, {
        answers,
      }).catch(() => { /* silent — try again on next change */ });
    }, SAVE_DEBOUNCE_MS);
    return () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
    };
  }, [answers, programSlug, isAuthed]);

  const visible = useMemo(() => {
    if (!data || data.showOnMemberPage === false) return [];
    const qs = Array.isArray(data.questions) ? data.questions : [];
    return qs
      .filter((q) => q && q.active !== false && q.questionId && q.questionText)
      .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));
  }, [data]);

  if (visible.length === 0) return null;

  const toggle = (qid) => {
    setExpanded((p) => ({ ...p, [qid]: !p[qid] }));
  };

  const setOption = (qid, optionId) => {
    setAnswers((prev) => {
      const next = { ...prev };
      // Tap-to-deselect — letting members un-pick keeps the response
      // panel discoverable but not coercive.
      if (next[qid] === optionId) {
        next[qid] = null;
      } else {
        next[qid] = optionId;
      }
      return next;
    });
  };

  return (
    <section data-testid="self-awareness-questions" style={{ marginTop: 32 }}>
      <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.22em", color: C.warmGray }}>
        ★ QUESTIONS TO ASK YOURSELF
      </div>
      <p
        style={{
          marginTop: 10,
          fontSize: 14,
          lineHeight: 1.6,
          color: C.warmGray,
          fontFamily: '"Instrument Serif", Georgia, serif',
          fontStyle: "italic",
          maxWidth: 620,
        }}
      >
        Three questions worth thinking about as you consider your options. Tap any question to see what your answer might mean.
      </p>

      <div style={{ marginTop: 18, display: "flex", flexDirection: "column", gap: 10 }}>
        {visible.map((q) => (
          <QuestionCard
            key={q.questionId}
            question={q}
            isOpen={!!expanded[q.questionId]}
            selectedOptionId={answers[q.questionId] || null}
            onToggle={() => toggle(q.questionId)}
            onSelect={(oid) => setOption(q.questionId, oid)}
            programAccent={programAccent}
          />
        ))}
      </div>
    </section>
  );
}

function QuestionCard({ question, isOpen, selectedOptionId, onToggle, onSelect, programAccent }) {
  const isInteractive = question.pattern === "interactive" && Array.isArray(question.options) && question.options.length > 0;
  const sortedOptions = useMemo(() => {
    if (!isInteractive) return [];
    return [...question.options]
      .filter((o) => o && o.optionId && o.label)
      .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));
  }, [question.options, isInteractive]);

  const selectedOption = isInteractive
    ? sortedOptions.find((o) => o.optionId === selectedOptionId)
    : null;

  const responseText = isInteractive
    ? (selectedOption?.response || "")
    : (question.response || "");

  return (
    <article
      data-testid={`self-awareness-card-${question.questionId}`}
      style={{
        background: C.pure,
        borderLeft: `3px solid ${C.peachDeep}`,
        boxShadow: isOpen ? "0 1px 0 rgba(15,14,13,0.06)" : "none",
        transition: "box-shadow 160ms ease",
      }}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={isOpen}
        data-testid={`self-awareness-toggle-${question.questionId}`}
        style={{
          width: "100%",
          background: "transparent",
          border: 0,
          padding: "18px 20px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 14,
          cursor: "pointer",
          textAlign: "left",
        }}
      >
        <span style={{ fontSize: 16, fontWeight: 700, color: C.ink, lineHeight: 1.4, letterSpacing: "-0.01em" }}>
          {question.questionText}
        </span>
        <span
          aria-hidden
          style={{
            fontSize: 14,
            color: C.warmGray,
            transition: "transform 200ms ease",
            transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
            flexShrink: 0,
            lineHeight: 1,
          }}
        >
          ▾
        </span>
      </button>

      {isOpen && (
        <div style={{ padding: "0 20px 20px" }}>
          <div style={{ height: 1, background: C.warmGray4, marginBottom: 16 }} />

          {isInteractive && question.introCopy && (
            <p style={{ margin: "0 0 14px", fontSize: 14, color: C.warmGray, lineHeight: 1.55 }}>
              {question.introCopy}
            </p>
          )}

          {isInteractive && (
            <div role="radiogroup" aria-label={question.questionText} style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {sortedOptions.map((opt) => {
                const selected = opt.optionId === selectedOptionId;
                return (
                  <button
                    key={opt.optionId}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    data-testid={`self-awareness-option-${question.questionId}-${opt.optionId}`}
                    onClick={() => onSelect(opt.optionId)}
                    style={{
                      textAlign: "left",
                      padding: "12px 14px",
                      background: selected ? C.peach : C.bone,
                      border: `1.5px solid ${selected ? programAccent : C.warmGray3}`,
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 12,
                      transition: "background 160ms ease, border-color 160ms ease",
                    }}
                  >
                    <Radio active={selected} accent={programAccent} />
                    <span style={{ fontSize: 14, color: C.ink, lineHeight: 1.5 }}>
                      {opt.label}
                    </span>
                  </button>
                );
              })}
            </div>
          )}

          {responseText ? (
            <div
              data-testid={`self-awareness-response-${question.questionId}`}
              style={{
                marginTop: isInteractive ? 18 : 0,
                paddingTop: isInteractive ? 18 : 0,
                borderTop: isInteractive ? `1px solid ${C.warmGray4}` : "none",
              }}
            >
              <ResponseText text={responseText} />
            </div>
          ) : isInteractive && !selectedOption ? (
            <p
              style={{
                marginTop: 18,
                fontSize: 13,
                color: C.warmGray,
                fontFamily: '"Instrument Serif", Georgia, serif',
                fontStyle: "italic",
                lineHeight: 1.55,
              }}
            >
              Pick what fits to see what your answer might mean.
            </p>
          ) : null}

          {/* Inline BMI calculator — saves the member a round-trip to a
              third-party tool and persists the result to their profile
              so it auto-fills the assessment height/weight step later. */}
          {question.questionId === "bmi" && (
            <BMICalculator programAccent={programAccent} />
          )}
        </div>
      )}
    </article>
  );
}

// Custom radio dot — peach-deep ring; filled with the program accent
// when active. Sits in lieu of native <input type="radio"> so styling
// is brand-consistent across browsers.
function Radio({ active, accent }) {
  return (
    <span
      aria-hidden
      style={{
        flexShrink: 0,
        width: 18,
        height: 18,
        borderRadius: "50%",
        border: `1.5px solid ${active ? accent : C.warmGray2}`,
        background: C.pure,
        marginTop: 2,
        position: "relative",
        display: "inline-block",
      }}
    >
      {active && (
        <span
          style={{
            position: "absolute",
            inset: 3,
            borderRadius: "50%",
            background: accent,
          }}
        />
      )}
    </span>
  );
}

// Render the admin-authored response. Plain text with two conventions:
//   • blank line   → paragraph break
//   • leading "• " → bullet list item
function ResponseText({ text }) {
  const blocks = useMemo(() => splitResponse(text), [text]);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {blocks.map((b, i) => {
        if (b.type === "list") {
          return (
            <ul key={i} style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 6 }}>
              {b.items.map((item, j) => (
                <li
                  key={j}
                  style={{
                    paddingLeft: 18,
                    position: "relative",
                    fontSize: 14,
                    lineHeight: 1.6,
                    color: C.ink,
                  }}
                >
                  <span style={{ position: "absolute", left: 0, color: C.peachDeep }}>•</span>
                  {item}
                </li>
              ))}
            </ul>
          );
        }
        return (
          <p key={i} style={{ margin: 0, fontSize: 14, lineHeight: 1.65, color: C.ink, whiteSpace: "pre-wrap" }}>
            {b.text}
          </p>
        );
      })}
    </div>
  );
}

// Split admin text into blocks. Adjacent lines starting with "•"
// collapse into a single bullet list; everything else becomes a
// paragraph. Blank lines act as paragraph dividers.
function splitResponse(raw) {
  const text = String(raw || "").trim();
  if (!text) return [];
  const blocks = [];
  for (const para of text.split(/\n{2,}/)) {
    const lines = para.split("\n").map((l) => l.trim()).filter(Boolean);
    if (lines.length === 0) continue;
    const isList = lines.every((l) => l.startsWith("•"));
    if (isList) {
      blocks.push({
        type: "list",
        items: lines.map((l) => l.replace(/^•\s*/, "")),
      });
    } else {
      blocks.push({ type: "para", text: para });
    }
  }
  return blocks;
}

// ============================================================================
// Inline BMI calculator. Imperial inputs (lbs / ft+in) — the US member
// audience thinks in those units.
//
// Persistence: localStorage is the immediate source of truth so the
// member's numbers survive navigation today. We *also* best-effort PUT
// to `/consumer/profile` — once the BE adds heightCm/weightKg/bmi to
// the profile model, that becomes the cross-device store automatically
// (no FE change). Today the PUT is silently ignored by the BE because
// the strict pydantic model drops unknown fields.
//
// Pre-fills from (in priority order): consumer profile → localStorage.
// Updates the assessment context immediately so Step 1 auto-fills the
// next time the member opens it.
// ============================================================================
const BMI_LS_KEY = "voya9.bmiCalc";

function readBmiLocal() {
  try {
    const raw = localStorage.getItem(BMI_LS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object") return parsed;
    return null;
  } catch {
    return null;
  }
}

function writeBmiLocal(snapshot) {
  try {
    localStorage.setItem(BMI_LS_KEY, JSON.stringify(snapshot));
  } catch { /* private browsing / quota — silent */ }
}

function BMICalculator({ programAccent }) {
  const { consumer, refreshProfile } = useAuth();
  const { updateQ } = useAssessment();
  const isAuthed = !!getToken();

  // Pre-fill priority: profile → localStorage. Profile wins because
  // it's authoritative across devices once the BE supports those fields.
  const local = useMemo(() => readBmiLocal(), []);
  const initFt = useMemo(
    () => cmToFtIn(consumer?.heightCm)?.ft || (local?.ft ?? ""),
    [consumer?.heightCm, local],
  );
  const initIn = useMemo(
    () => {
      const fromProfile = cmToFtIn(consumer?.heightCm);
      if (fromProfile?.in !== "" && fromProfile?.in != null) return fromProfile.in;
      return local?.inches ?? "";
    },
    [consumer?.heightCm, local],
  );
  const initLbs = useMemo(
    () => kgToLbs(consumer?.weightKg) || (local?.lbs ?? ""),
    [consumer?.weightKg, local],
  );

  const [ft, setFt] = useState(initFt);
  const [inches, setInches] = useState(initIn);
  const [lbs, setLbs] = useState(initLbs);

  // Keep local state in sync if the profile loads after the calc mounts
  // (auth context can hydrate after the page renders).
  useEffect(() => {
    if (initFt && !ft) setFt(initFt);
    if ((initIn || initIn === 0) && !inches) setInches(initIn);
    if (initLbs && !lbs) setLbs(initLbs);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initFt, initIn, initLbs]);

  const heightCm = useMemo(() => ftInToCm(ft, inches), [ft, inches]);
  const weightKg = useMemo(() => lbsToKg(lbs), [lbs]);
  const bmi = useMemo(() => calcBmi(heightCm, weightKg), [heightCm, weightKg]);

  // Persist to localStorage on every meaningful change so the values
  // survive navigation immediately. We persist the imperial inputs
  // (what the member sees) along with the derived metric values for
  // any consumer that wants to read either.
  useEffect(() => {
    if (!ft && !inches && !lbs) return;
    writeBmiLocal({
      ft, inches, lbs,
      heightCm: heightCm || null,
      weightKg: weightKg || null,
      bmi: bmi || null,
      updatedAt: new Date().toISOString(),
    });
  }, [ft, inches, lbs, heightCm, weightKg, bmi]);

  // Push to assessment context immediately (in-memory, free) so the
  // assessment Step 1 prefills if the member opens it next.
  //
  // ⚠ The assessment provider's `updateQ` is recreated on every state
  // change. Including it in the effect deps would cause an infinite
  // render loop (updateQ → setData → new value → new updateQ ref →
  // effect fires → updateQ → …). We hold the function in a ref so
  // the effect deps stay stable and we only push when the values
  // actually change.
  const updateQRef = useRef(updateQ);
  useEffect(() => { updateQRef.current = updateQ; }, [updateQ]);
  const lastPushed = useRef(null);
  useEffect(() => {
    if (!heightCm || !weightKg || !bmi) return;
    const snapshot = `${heightCm}|${weightKg}|${bmi}`;
    if (snapshot === lastPushed.current) return;
    lastPushed.current = snapshot;
    updateQRef.current({ heightCm, weightKg, bmi });
  }, [heightCm, weightKg, bmi]);

  // Best-effort PUT to /consumer/profile — silently ignored by the BE
  // today (their pydantic model drops unknown fields), but works the
  // moment they add `heightCm`, `weightKg`, `bmi` to the model.
  const saveTimer = useRef(null);
  const lastSaved = useRef(null);
  useEffect(() => {
    if (!isAuthed) return undefined;
    if (!heightCm || !weightKg || !bmi) return undefined;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      const snapshot = `${heightCm}|${weightKg}|${bmi}`;
      if (snapshot === lastSaved.current) return;
      lastSaved.current = snapshot;
      apiPut("/consumer/profile", {
        heightCm: Number(heightCm),
        weightKg: Number(weightKg),
        bmi: Number(bmi),
      })
        .then(() => refreshProfile?.())
        .catch(() => { /* silent — try again on next change */ });
    }, 800);
    return () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
    };
  }, [heightCm, weightKg, bmi, isAuthed, refreshProfile]);

  const category = bmiCategory(bmi);

  return (
    <div
      data-testid="bmi-calculator"
      style={{
        marginTop: 18,
        padding: "16px 18px",
        background: C.bone,
        border: `1px solid ${C.warmGray3}`,
      }}
    >
      <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.22em", color: programAccent }}>
        ★ CALCULATE YOUR BMI
      </div>
      <p style={{ margin: "6px 0 14px", fontSize: 13, color: C.warmGray, lineHeight: 1.55 }}>
        Enter your weight and height. We'll save it to your profile so it pre-fills your assessment.
      </p>

      <div style={{ display: "grid", gridTemplateColumns: "1.2fr 1fr 1fr", gap: 10 }}>
        <BmiField label="WEIGHT (LBS)" testId="bmi-weight">
          <input
            inputMode="numeric"
            value={lbs}
            onChange={(e) => setLbs(e.target.value.replace(/[^\d]/g, "").slice(0, 3))}
            placeholder="180"
            data-testid="bmi-weight-input"
            style={bmiInputStyle}
          />
        </BmiField>
        <BmiField label="HEIGHT (FT)" testId="bmi-ft">
          <input
            inputMode="numeric"
            value={ft}
            onChange={(e) => setFt(e.target.value.replace(/[^\d]/g, "").slice(0, 1))}
            placeholder="5"
            data-testid="bmi-ft-input"
            style={bmiInputStyle}
          />
        </BmiField>
        <BmiField label="HEIGHT (IN)" testId="bmi-in">
          <input
            inputMode="numeric"
            value={inches}
            onChange={(e) => setInches(e.target.value.replace(/[^\d]/g, "").slice(0, 2))}
            placeholder="10"
            data-testid="bmi-in-input"
            style={bmiInputStyle}
          />
        </BmiField>
      </div>

      {bmi ? (
        <div
          data-testid="bmi-result"
          style={{
            marginTop: 14,
            padding: "12px 14px",
            background: C.pure,
            borderLeft: `3px solid ${programAccent}`,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "baseline",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          <div>
            <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.22em", color: programAccent }}>
              YOUR BMI
            </div>
            <div style={{ marginTop: 4, fontSize: 11, color: C.warmGray, letterSpacing: "0.04em" }}>
              {category}
            </div>
          </div>
          <div style={{ fontSize: 32, fontWeight: 800, letterSpacing: "-0.02em", color: C.ink }}>
            {bmi}
          </div>
        </div>
      ) : (
        <p
          style={{
            marginTop: 12,
            fontSize: 12,
            color: C.warmGray,
            fontStyle: "italic",
            fontFamily: '"Instrument Serif", Georgia, serif',
          }}
        >
          Fill all three fields to see your BMI.
        </p>
      )}
    </div>
  );
}

function BmiField({ label, testId, children }) {
  return (
    <div data-testid={testId}>
      <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: "0.18em", color: C.warmGray, marginBottom: 4 }}>
        {label}
      </div>
      <div style={{ padding: "10px 12px", background: C.pure, border: `1.5px solid ${C.warmGray3}` }}>
        {children}
      </div>
    </div>
  );
}

const bmiInputStyle = {
  width: "100%",
  border: 0,
  outline: "none",
  fontFamily: '"DM Sans", sans-serif',
  fontSize: 16, // 16+ to prevent iOS auto-zoom
  fontWeight: 700,
  color: "#0F0E0D",
  padding: 0,
  background: "transparent",
};

// CDC adult BMI categories — used for the small label under the result.
function bmiCategory(bmi) {
  const n = Number(bmi);
  if (!n) return "";
  if (n < 18.5) return "Below normal range";
  if (n < 25) return "Normal range";
  if (n < 30) return "Overweight range";
  if (n < 35) return "Obese class I";
  if (n < 40) return "Obese class II";
  return "Obese class III";
}
